import React, {useEffect, useRef, useState} from "react";
import Block, {Section} from "../Block";
import {useTranslation} from "react-i18next";
import {Button, Col, Form, OverlayTrigger, Row} from "react-bootstrap";
import {useForm, Yup} from "../forms/form";
import {BoolSelect, FormGroup} from "../components";
import Select from "../forms/inputs/Select";
import CmpLayoutPreview from "./CmpLayoutPreview";
import FileInput from "../forms/inputs/FileInput";
import {useFormattedLanguage} from "../utils";
import ColorShowItem from "./ColorShowItem";
import pick from "lodash/pick"

export default function EditPopupConfig({cmpConfig, setSubmitButtonProps, onSubmit, handleSuccess, params, preparedCmpConfigContent = []}) {
  const { t } = useTranslation();

  if (!cmpConfig) {
    return <></>
  }

  const initialValues = {
    publisherName: '',
    privacyPolicyUrl: '',
    dpoEmail: '',
    dpoPostalAddress: '',
    languages: [],
    defaultLanguage: '',
    allowRetry: '',
    optinValidity: '',
    includeAllIabVendors: '',
    content: [],
    linkColor: '',
    activeSwitchColor: '',
    backgroundColor: '',
    buttonColor: '',
    headerColor: '',
    textColor: '',
    fontUrl: '',
    fontSelector: '',
    defaultTcfV2ConsentRequestLayoutVersion: '',
    logoUrl: '',
    logoFilename: ''
  };
  const validationSchema = Yup.object().shape({

  })

  const { formData, getInputProps, formProps, handleInputChange } = useForm({
    defaultObject: cmpConfig,
    initialValues: initialValues,
    validationSchema: validationSchema,
    setSubmitButtonProps: setSubmitButtonProps,
    onSubmit: (data) => {
      onSubmit({...params, config: data}).unwrap().then(handleSuccess)
    },
  });

  const formatLanguage = useFormattedLanguage(t)
  const formatLanguagesOptions = languages => languages?.map(lang => ({label: formatLanguage(lang), value: lang}))
  const boolTranslate = (bool) => typeof(bool) == 'boolean' ? t(`${bool}`) : null

  const colorShowItemProps = {
    editable: true,
    labelStyle: {
      paddingLeft: 0,
      fontWeight: "inherit"
    }
  }

  return <Form {...formProps}>
    <Block title={t("cmp.popup.configBlockTitle")}>
      <Section title={t("cmp.popup.general")}>
        <FormGroup input={"text"} label={t("cmp.popup.publisherName")} {...getInputProps("publisherName")} placeholder={cmpConfig.inheritedConfig?.publisherName}/>
        <FormGroup input={"text"} label={t("cmp.popup.privacyPolicyUrl")} {...getInputProps("privacyPolicyUrl")} placeholder={cmpConfig.inheritedConfig?.privacyPolicyUrl}/>
      </Section>
      <Section title={t("cmp.popup.dpoContact")}>
        <FormGroup input={"text"} label={t("cmp.popup.dpoEmail")} {...getInputProps("dpoEmail")} placeholder={cmpConfig.inheritedConfig?.dpoEmail}/>
        <FormGroup input={"textarea"} rows={3} label={t("cmp.popup.dpoPostalAddress")} {...getInputProps("dpoPostalAddress")} placeholder={cmpConfig.inheritedConfig?.dpoPostalAddress}/>
      </Section>
      <Section title={t("cmp.popup.languages")}>
        <FormGroup label={t("cmp.popup.languages")} {...getInputProps("languages")} placeholder={cmpConfig.inheritedConfig?.languages?.map(formatLanguage)?.join(', ')}>
          <Select isMulti isClearable options={formatLanguagesOptions(['fr', 'en'])}/>
        </FormGroup>
        <FormGroup label={t("cmp.popup.defaultLanguage")} {...getInputProps("defaultLanguage")} placeholder={formatLanguage(cmpConfig.inheritedConfig?.defaultLanguage)}>
          <Select isClearable options={formatLanguagesOptions(formData.languages.length > 0 ? formData.languages : cmpConfig.inheritedConfig?.languages)}/>
        </FormGroup>
      </Section>
      <Section title={t("cmp.popup.retry")}>
        <FormGroup input={BoolSelect} label={t("cmp.popup.allowRetry")} {...getInputProps("allowRetry")} placeholder={boolTranslate(cmpConfig.inheritedConfig?.allowRetry)}>
          <BoolSelect isClearable/>
        </FormGroup>
        <FormGroup input={"text"} label={t("cmp.popup.optinValidity")} {...getInputProps("optinValidity")} placeholder={cmpConfig.inheritedConfig?.optinValidity}/>
      </Section>
      <Section title={t("cmp.vendors.tabName")}>
        <FormGroup label={t("cmp.popup.includeAllIabVendors")} {...getInputProps("includeAllIabVendors")} placeholder={boolTranslate(cmpConfig.inheritedConfig?.includeAllIabVendors)}>
          <BoolSelect isClearable/>
        </FormGroup>
      </Section>
    </Block>

    <Block title={t("cmp.popup.contentBlockTitle")}>
      {preparedCmpConfigContent.map(content => {
        return <Section key={content.language} title={formatLanguage(content.language)}>
          {content.attributes?.map(attribute => {
            return <FormGroup input={"textarea"}
                              key={attribute.name}
                              rows={attribute.name == 'body' ? 6 : 1}
                              label={t(`cmp.${attribute.name}`)}
                              name={`${content.language}.${attribute.name}`}
                              onChange={(v) => {
                                const filtered = formData.content.filter(el => el.language != content.language || el.name != attribute.name)
                                handleInputChange([...filtered, {language: content.language, name: attribute.name, content: v.target.value}], 'content')
                              }}
                              value={formData.content.find(el => el.language == content.language && el.name == attribute.name)?.content || ""}
                              placeholder={attribute.inheritedContent}/>
          })}
        </Section>
      })}
    </Block>

    <Block title={t("cmp.popup.layoutBlockTitle")}>
      <Row>
        <Col md={8}>
          <Row>
            <Col md={6}>
              <ColorShowItem {...colorShowItemProps} {...getInputProps("linkColor")} placeholder={cmpConfig.inheritedConfig?.linkColor}/>
              <ColorShowItem {...colorShowItemProps} {...getInputProps("activeSwitchColor")} placeholder={cmpConfig.inheritedConfig?.activeSwitchColor}/>
              <ColorShowItem {...colorShowItemProps} {...getInputProps("backgroundColor")} placeholder={cmpConfig.inheritedConfig?.backgroundColor}/>
            </Col>
            <Col md={6}>
              <ColorShowItem {...colorShowItemProps} {...getInputProps("buttonColor")} placeholder={cmpConfig.inheritedConfig?.buttonColor}/>
              <ColorShowItem {...colorShowItemProps} {...getInputProps("headerColor")} placeholder={cmpConfig.inheritedConfig?.headerColor}/>
              <ColorShowItem {...colorShowItemProps} {...getInputProps("textColor")} placeholder={cmpConfig.inheritedConfig?.textColor}/>
            </Col>
          </Row>
          <Row>
            <FormGroup
              as={Col}
              style={{marginTop: "20px"}}
              md={12}
              input={FileInput}
              label={t("cmp.popup.logo")}
              inputProps={{logoFilenameInputProps: getInputProps("logoFilename")}}
              {...getInputProps("logoUrl")}
              placeholder={cmpConfig.inheritedConfig?.logoFilename}
              text={t("cmp.popup.logoText")}/>
            <FormGroup
              as={Col}
              md={12}
              input={"text"}
              label={t("cmp.popup.fontUrl")}
              {...getInputProps("fontUrl")}
              placeholder={cmpConfig.inheritedConfig?.fontUrl}
              text={t("cmp.popup.fontUrlText")}/>
            <FormGroup
              as={Col}
              md={12}
              input={"text"}
              label={t("cmp.popup.fontSelector")}
              {...getInputProps("fontSelector")}
              placeholder={cmpConfig.inheritedConfig?.fontSelector}
              text={t("cmp.popup.fontSelectorText")}/>
            <FormGroup
              as={Col}
              md={12}
              input={"text"}
              label={t("cmp.popup.defaultTcfV2ConsentRequestLayoutVersion")}
              {...getInputProps("defaultTcfV2ConsentRequestLayoutVersion")}
              placeholder={cmpConfig.inheritedConfig?.defaultTcfV2ConsentRequestLayoutVersion}/>
          </Row>
        </Col>
        <Col md={4}>
          <Section title={"Preview"}>
            <CmpLayoutPreview {...mergedLayoutProperties(formData, cmpConfig.inheritedConfig)}/>
          </Section>
        </Col>
      </Row>
    </Block>
  </Form>
}

const mergedLayoutProperties = (config, inheritedConfig = {}) => {
  let mergedConfig = pick(config, ['linkColor', 'activeSwitchColor', 'backgroundColor', 'buttonColor', 'headerColor', 'textColor', 'fontUrl', 'fontSelector', 'logoUrl'])
  for (const attribute in mergedConfig) {
    if (!mergedConfig[attribute]) {
      mergedConfig[attribute] = inheritedConfig[attribute]
    }
  }
  return mergedConfig
}

